// Utility functions for Indian number formatting and validation

/**
 * Format number in Indian numbering system (lakhs, crores)
 * @param value - Number to format
 * @param includeDecimals - Whether to include decimal places
 * @returns Formatted string
 */
export const formatIndianNumber = (value: number | string, includeDecimals: boolean = true): string => {
  if (!value && value !== 0) return '';
  
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '';

  // Handle decimals
  const decimalPlaces = includeDecimals ? 2 : 0;
  const fixedNum = num.toFixed(decimalPlaces);
  
  // Split into integer and decimal parts
  const [integerPart, decimalPart] = fixedNum.split('.');
  
  // Apply Indian numbering system
  const lastThree = integerPart.slice(-3);
  const otherNumbers = integerPart.slice(0, -3);
  
  let formattedInteger;
  if (otherNumbers !== '') {
    formattedInteger = otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + lastThree;
  } else {
    formattedInteger = lastThree;
  }
  
  // Combine with decimal part if needed
  return includeDecimals && decimalPart ? `${formattedInteger}.${decimalPart}` : formattedInteger;
};

/**
 * Parse Indian formatted number string to number
 * @param value - Formatted string
 * @returns Number value
 */
export const parseIndianNumber = (value: string): number => {
  if (!value) return 0;
  
  // Remove all commas and parse
  const cleanValue = value.replace(/,/g, '');
  const parsed = parseFloat(cleanValue);
  
  return isNaN(parsed) ? 0 : parsed;
};

/**
 * Format currency in Indian Rupees
 * @param value - Amount to format
 * @param includeSymbol - Whether to include ₹ symbol
 * @returns Formatted currency string
 */
export const formatIndianCurrency = (value: number | string, includeSymbol: boolean = true): string => {
  const formatted = formatIndianNumber(value, true);
  return includeSymbol ? `₹${formatted}` : formatted;
};

/**
 * Validate if string is a valid number
 * @param value - String to validate
 * @returns Boolean indicating validity
 */
export const isValidNumber = (value: string): boolean => {
  if (!value.trim()) return false;
  const cleanValue = value.replace(/,/g, '');
  return !isNaN(parseFloat(cleanValue)) && isFinite(parseFloat(cleanValue));
};

/**
 * Clean number input (remove invalid characters)
 * @param value - Input string
 * @returns Cleaned string
 */
export const cleanNumberInput = (value: string): string => {
  // Allow digits, decimal point, and commas
  return value.replace(/[^0-9.,]/g, '');
};

/**
 * Format number as user types (progressive formatting)
 * @param value - Current input value
 * @returns Formatted value
 */
export const formatAsUserTypes = (value: string): string => {
  const cleaned = cleanNumberInput(value);
  if (!cleaned) return '';
  
  // Don't format if user is typing decimal places
  if (cleaned.endsWith('.') || cleaned.includes('.') && cleaned.split('.')[1].length <= 2) {
    const parts = cleaned.split('.');
    const integerPart = parts[0].replace(/,/g, '');
    const formatted = formatIndianNumber(integerPart, false);
    return parts[1] !== undefined ? `${formatted}.${parts[1]}` : `${formatted}.`;
  }
  
  return formatIndianNumber(cleaned, false);
};

// Alias for formatIndianCurrency for convenience
export const formatCurrency = formatIndianCurrency;